<template>
  <CardHeader
    :icon="icon"
    :length="length === -1 ? undefined : length"
    :on-delete-all="enableDeleteAll ? onDeleteAll : undefined"
    :on-new="disableNew ? undefined : onNew"
    :on-refresh="onRefresh"
    :subtitle="description ? description : undefined"
    :title="title"
    :loading="loading"
  >
    <template #title>
      <slot name="title">{{ title }}</slot>
    </template>
    <template #before>
      <div
        class="search-input-wrapper hidden items-center justify-center md:flex"
        v-if="hasFilter"
      >
        <span class="relative">
          <i
            class="pi pi-search absolute left-3 top-2/4 -mt-2 text-surface-400 dark:text-surface-600"
          />
          <i
            class="pi pi-spin pi-spinner absolute right-3 top-2/4 -mt-2 text-surface-400 dark:text-surface-600"
            v-if="loading"
          />

          <FormInput
            class="pl-10 pr-10"
            :data-buffering="buffering ? '1' : '0'"
            :data-loading="loading ? '1' : '0'"
            :value="searchFilter"
            data-id="data-table-search-filter"
            :placeholder="'Search' + '...'"
            size="xs"
            @change="onFilterChange"
          />
        </span>
      </div>
      <!-- filters -->
      <ButtonGroup v-if="hasFilters">
        <FormDropDown size="sm" no-button position="bottom-left">
          <template #button-content>
            <span
              class="is-pointer px-4 py-1.5 text-xs hover:bg-gray-100"
              data-id="data-table-filters"
              :class="{ 'text-green-500': hasActiveFilters }"
              ><Icon icon="filter"
            /></span>
          </template>
          <section class="flex flex-col gap-2">
            <FormDropdownFormItem
              v-if="pageSize"
              :data-page-size="pageSize"
              :label="'Page size'"
            >
              <FormSelect
                size="xs"
                inline
                data-select="page-size"
                :options="pageSizes"
                :value="pageSize.toString(10)"
                @change="onPageSize"
              />
            </FormDropdownFormItem>
            <FormDropdownFormItem
              v-if="hasUnseenOnlyToggle"
              :data-unseen-only="unseenOnly ? '1' : '0'"
              :label="'Unseen'"
            >
              <FormCheckbox
                class="flex"
                data-toggle="unseen-only"
                :switch="true"
                :value="unseenOnly"
                @change="onUnseenOnly"
              />
            </FormDropdownFormItem>
            <FormDropdownFormItem
              v-if="hasUnreadOnlyToggle"
              :data-unreadonly="unreadOnly ? '1' : '0'"
              :label="'Unread'"
            >
              <FormCheckbox
                class="flex"
                data-toggle="unread-only"
                :switch="true"
                :value="unreadOnly"
                @change="onUnreadOnly"
              />
            </FormDropdownFormItem>
            <FormDropdownFormItem
              v-if="$slots.filter && filterLabel"
              label="Filter"
            >
              <slot name="filter" />
            </FormDropdownFormItem>
            <section
              class="flex flex-col gap-2"
              v-else-if="$slots.filter && !filterLabel"
            >
              <slot name="filter" />
            </section>
          </section>
        </FormDropDown>
      </ButtonGroup>
      <!-- has more -->
      <ButtonGroup v-if="hasMore">
        <FormDropDown size="sm" no-button position="bottom-left">
          <template #button-content>
            <span
              class="is-pointer px-4 py-1.5 text-xs hover:bg-gray-100"
              data-id="data-table-more"
              :class="{ 'text-green-500': hasActiveFilters }"
              ><Icon icon="cog"
            /></span>
          </template>
          <section class="flex flex-col gap-2">
            <slot name="more" />
          </section>
        </FormDropDown>
      </ButtonGroup>
      <div
        class="mr-3 hidden lg:flex"
        v-if="tagsOptions && tagsOptions.length > 0"
      >
        <FormSelect
          v-model="activeTagRef"
          :options="tagsOptions"
          :data-tags-list="tagsList"
          data-id="data-table-tag-select"
          size="xs"
          @change="onTagsChange"
        />
      </div>
      <div
        class="date-picker-wrapper d-sm-flex mr-3 hidden"
        v-if="enableDatePicker"
      >
        <DateRangePicker @change="onDateChange" />
      </div>
    </template>
  </CardHeader>
</template>
<script setup lang="ts">
import CardHeader from '~/components/CardHeader.vue';
import DateRangePicker from '~/components/DateRangePicker.vue';
import type { DateRange } from '~/lib/services/TimeService';
import { onMounted, ref, computed, watch, useSlots } from 'vue';
import FormInput from '~/components/form/FormInput.vue';
import FormSelect from '~/components/form/FormSelect.vue';
import Icon from '~/components/form/Icon.vue';
import FormCheckbox from '~/components/form/FormCheckbox.vue';
import FormDropDown from '~/components/form/FormDropDown.vue';
import ButtonGroup from '~/components/form/ButtonGroup.vue';
import FormDropdownFormItem from '~/components/form/FormDropdownFormItem.vue';
import type { MsIcon } from '~/lib/services/types';
import { getLogger } from '~/lib/services/getLogger';

const log = getLogger('DataTableHeader');

let timeout: NodeJS.Timeout;

const activeTagRef = ref<string | null>(null);
const unreadOnly = ref<boolean>(false);
const unseenOnly = ref<boolean>(false);

const hasActiveFilters = computed(() => unreadOnly.value || unseenOnly.value);
const buffering = computed<boolean>(() => {
  return !!timeout;
});
const onPageSize = (e: string) => {
  emit('pagesize', parseInt(e, 10));
};

const props = withDefaults(
  defineProps<{
    loading: boolean;
    title: string;
    disableNew?: boolean;
    enableDeleteAll?: boolean;
    activeTag?: string;
    tags?: string[];
    enableDatePicker?: boolean;
    hasUnreadOnlyToggle?: boolean;
    hasUnseenOnlyToggle?: boolean;
    noCard?: boolean;
    length?: number;
    description?: string;
    icon?: MsIcon;
    hasFilter?: boolean;
    searchFilter?: string;
    pageSize?: number;
    filterLabel?: boolean;
  }>(),
  {
    filterLabel: true,
    pageSize: undefined,
    disableNew: false,
    enableDeleteAll: false,
    activeTag: '',
    tags: undefined,
    enableDatePicker: false,
    hasUnreadOnlyToggle: false,
    hasUnseenOnlyToggle: false,
    noCard: false,
    length: 0,
    description: '',
    icon: undefined,
    hasFilter: false,
    searchFilter: '',
  },
);
const slots = useSlots();
const hasMore = computed(() => slots.more);
const hasFilters = computed(
  () =>
    props.hasUnseenOnlyToggle ||
    props.hasUnreadOnlyToggle ||
    props.pageSize ||
    slots.filter,
);
const activeTagP = computed(() => props.activeTag);

const pageSizes = [10, 20, 50, 100].map((it) => ({
  value: it.toString(),
  text: it.toString(),
}));

const emit = defineEmits<{
  (e: 'dateRange', p: DateRange): void;
  (e: 'tagsChanged', p: string): void;
  (e: 'filterChange', p: string): void;
  (e: 'unseenOnly', p: boolean): void;
  (e: 'unreadOnly', p: boolean): void;
  (e: 'new'): void;
  (e: 'refresh'): void;
  (e: 'deleteAll'): void;
  (e: 'pagesize', p: number): void;
}>();

const onDateChange = (date: DateRange) => {
  emit('dateRange', date);
};

const onTagsChange = (payload: string) => {
  activeTagRef.value = payload;
  emit('tagsChanged', payload);
};

onMounted(async () => {
  activeTagRef.value = props.activeTag ?? '';
});

const tagsOptions = computed(() => {
  if (props.tags && props.tags.length) {
    return [
      { value: '', text: 'All tags' },
      ...props.tags.map((tag) => ({ value: tag, text: tag })),
    ];
  } else {
    return [];
  }
});

const tagsList = computed(() => {
  return props.tags?.join(',') ?? '';
});

const onFilterChange = (payload: string) => {
  if (timeout) {
    clearTimeout(timeout);
  }
  timeout = setTimeout(() => {
    emit('filterChange', payload);
    clearTimeout(timeout);
  }, 1000);
};

const onUnseenOnly = (newValue: boolean) => {
  unseenOnly.value = newValue;
  emit('unseenOnly', newValue);
};

const onUnreadOnly = (newValue: boolean) => {
  unreadOnly.value = newValue;
  emit('unreadOnly', newValue);
};

const onDeleteAll = () => {
  log('deleteAll');
  emit('deleteAll');
};

const onNew = () => {
  emit('new');
};

const onRefresh = () => {
  emit('refresh');
};
watch(activeTagP, (payload: string) => {
  activeTagRef.value = payload;
});
</script>
